// Anchor math + persistence for the draggable Feedback widget.
// Pure helpers so they can be unit-tested without a DOM.

export const STORAGE_KEY = 'civix_feedback_widget_position';
export const EDGE_OFFSET = 16; // px gap kept from any viewport edge
export const DEFAULT_ANCHOR = 'bottom-right';

// 8 snap anchors: 4 corners + 4 edge midpoints.
export const ANCHORS = [
  'top-left',
  'top-center',
  'top-right',
  'left-center',
  'right-center',
  'bottom-left',
  'bottom-center',
  'bottom-right',
];

function isValidAnchor(name) {
  return ANCHORS.includes(name);
}

// Resolve the top-left pixel coordinate for an anchor given the viewport
// and the element box, keeping EDGE_OFFSET away from every edge.
export function anchorToPoint(anchor, viewport, box) {
  const { width: vw, height: vh } = viewport;
  const { width: bw, height: bh } = box;
  const left = EDGE_OFFSET;
  const right = Math.max(EDGE_OFFSET, vw - bw - EDGE_OFFSET);
  const centerX = Math.max(EDGE_OFFSET, Math.round((vw - bw) / 2));
  const top = EDGE_OFFSET;
  const bottom = Math.max(EDGE_OFFSET, vh - bh - EDGE_OFFSET);
  const centerY = Math.max(EDGE_OFFSET, Math.round((vh - bh) / 2));

  switch (anchor) {
    case 'top-left': return { x: left, y: top };
    case 'top-center': return { x: centerX, y: top };
    case 'top-right': return { x: right, y: top };
    case 'left-center': return { x: left, y: centerY };
    case 'right-center': return { x: right, y: centerY };
    case 'bottom-left': return { x: left, y: bottom };
    case 'bottom-center': return { x: centerX, y: bottom };
    case 'bottom-right':
    default: return { x: right, y: bottom };
  }
}

// Pick the anchor whose resolved point is closest to the given point
// (the element's top-left after a drag).
export function nearestAnchor(point, viewport, box) {
  let best = DEFAULT_ANCHOR;
  let bestDist = Infinity;
  for (const anchor of ANCHORS) {
    const p = anchorToPoint(anchor, viewport, box);
    const dx = p.x - point.x;
    const dy = p.y - point.y;
    const dist = dx * dx + dy * dy;
    if (dist < bestDist) {
      bestDist = dist;
      best = anchor;
    }
  }
  return best;
}

// Clamp a free-drag point so the box stays EDGE_OFFSET from the edges.
export function clampPoint(point, viewport, box) {
  const maxX = Math.max(EDGE_OFFSET, viewport.width - box.width - EDGE_OFFSET);
  const maxY = Math.max(EDGE_OFFSET, viewport.height - box.height - EDGE_OFFSET);
  return {
    x: Math.min(Math.max(point.x, EDGE_OFFSET), maxX),
    y: Math.min(Math.max(point.y, EDGE_OFFSET), maxY),
  };
}

export function loadAnchor() {
  try {
    const raw = typeof localStorage !== 'undefined' && localStorage.getItem(STORAGE_KEY);
    return isValidAnchor(raw) ? raw : DEFAULT_ANCHOR;
  } catch {
    return DEFAULT_ANCHOR;
  }
}

export function saveAnchor(anchor) {
  try {
    if (isValidAnchor(anchor) && typeof localStorage !== 'undefined') {
      localStorage.setItem(STORAGE_KEY, anchor);
    }
  } catch {
    /* ignore storage failures (private mode, quota) */
  }
}

export function clearAnchor() {
  try {
    if (typeof localStorage !== 'undefined') localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}
